import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import { ReferralSummary } from './ReferralSummary';
import { IMedicalReferral, requestFile } from 'API/medicalReferrals';
import {
  AccordionArrowPurple,
  AccordionArrowPurpleClose,
  Download2,
} from 'Images';
import { useDictionary, useIsDesktop } from 'Hooks';
import { ReferralDetails } from './ReferralDetails';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useState } from 'react';
import { useLoader } from 'Hooks/useLoader';
import { downloadFile } from 'Utils';

interface IReferralAccordion {
  referral: IMedicalReferral;
}

export const ReferralAccordion = ({ referral }: IReferralAccordion) => {
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.MedicalReferrals);
  const { setIsLoading, isLoading } = useLoader();

  const handleAccordionChange = (
    event: React.SyntheticEvent,
    expanded: boolean
  ) => {
    setAccordionExpanded(expanded);
  };

  const downloadReferralPDF = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setIsLoading(false);
    event.stopPropagation();
    if (!referral.id) return;

    const referralFile = await requestFile(referral.id);
    if (referralFile?.fileContentBase64)
      downloadFile(referralFile?.fileContentBase64, 'pdf', referral.part);

    setIsLoading(true);
  };

  return (
    <Stack position={'relative'}>
      <Accordion
        expanded={accordionExpanded}
        aria-expanded={accordionExpanded}
        aria-controls={`referral-content-${referral.id}`}
        onChange={handleAccordionChange}
        disableGutters
        elevation={0}
        sx={{
          bgcolor: '#fff',
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'primary.shade_100',
          position: 'relative',
          paddingBottom: isDesktop ? '0' : '100px',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          sx={{
            gap: isDesktop ? '0' : '22px',
            position: 'static',
            flexDirection: isDesktop ? 'row' : 'column',
            alignItems: isDesktop ? undefined : 'flex-start',
            padding: isDesktop ? '44px 24px 24px 24px' : '44px 16px 0px 16px',
            '.MuiAccordionSummary-content': {
              margin: 0,
              width: isDesktop ? undefined : '100%',
            },
            '.MuiAccordionSummary-expandIconWrapper': {
              position: isDesktop ? 'static' : 'absolute',
              bottom: '50px',
            },
          }}
          expandIcon={
            isDesktop ? (
              <>
                {!accordionExpanded && (
                  <Avatar
                    src={AccordionArrowPurple}
                    alt={''}
                    sx={{
                      width: '40px',
                      height: '40px',
                    }}
                  />
                )}
                {accordionExpanded && (
                  <Avatar
                    src={AccordionArrowPurpleClose}
                    alt={''}
                    sx={{
                      width: '35px',
                      height: '35px',
                      rotate: '180deg',
                    }}
                  />
                )}
              </>
            ) : (
              <></>
            )
          }
        >
          <ReferralSummary
            referral={referral}
            downloadPdf={downloadReferralPDF}
            isLoading={isLoading}
          />
        </AccordionSummary>
        <AccordionDetails
          sx={{
            padding: isDesktop ? '32px 32px 32px 24px' : '0',
            position: 'relative',
          }}
          id={`referral-content-${referral.id}`}
          tabIndex={-1}
        >
          <ReferralDetails
            statusDate={referral.updateRowDate || null}
            supplierName={referral.supplierName}
            requestNumber={referral.requestNumber}
            isActive={referral.isActive}
            invalidationDate={referral.invalidationDate}
            linkedApplyNumbers={referral.linkedApplyNumbers || []}
          />
        </AccordionDetails>
      </Accordion>
      {!isDesktop && (
        <>
          <Stack
            alignItems={'center'}
            bottom={'50px'}
            left={0}
            right={0}
            position={'absolute'}
            sx={{ cursor: 'pointer' }}
            onClick={() => setAccordionExpanded(!accordionExpanded)}
          >
            {!accordionExpanded && (
              <Avatar
                src={AccordionArrowPurple}
                alt={''}
                sx={{
                  width: '40px',
                  height: '40px',
                }}
              />
            )}
            {accordionExpanded && (
              <Avatar
                src={AccordionArrowPurpleClose}
                alt={''}
                sx={{
                  width: '30px',
                  height: '30px',
                }}
              />
            )}
          </Stack>
          <Button
            onClick={downloadReferralPDF}
            sx={{
              position: 'absolute',
              bottom: '0',
              left: '0',
              backgroundColor: 'primary.shade_100',
              border: '1px solid rgba(183, 204, 255, 0.2)',
              borderRadius: '0',
              borderEndEndRadius: '16px',
              borderEndStartRadius: '16px',
              padding: '12px 24px',
              width: '100%',
              height: '42px',
              gap: '10px',
              justifyContent: 'flex-end',
              '&:hover': {
                backgroundColor: 'primary.shade_100',
              },
            }}
          >
            <Avatar
              alt={''}
              src={Download2}
              sx={{ width: '20px', height: '20px' }}
            />
            <Typography variant={'body5'}>
              {dictionary.referral.watchFileFull}
            </Typography>
          </Button>
        </>
      )}
    </Stack>
  );
};
